import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import { AISidebar, AIRecommendationPanel } from '../components/ai';
import { Info, Users, Heart, Leaf, TrendingUp, AlertCircle } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
// Mock data for segments
const segmentData = [{
  name: 'Studenti',
  value: 145,
  color: '#1a4d2e'
}, {
  name: 'Professionisti',
  value: 98,
  color: '#2d7a4f'
}, {
  name: 'Famiglie',
  value: 67,
  color: '#4aa770'
}, {
  name: 'Senior',
  value: 42,
  color: '#7bc99c'
}];
const segmentTrendData = [{
  month: 'Gen',
  studenti: 120,
  professionisti: 85,
  famiglie: 55,
  senior: 35
}, {
  month: 'Feb',
  studenti: 128,
  professionisti: 90,
  famiglie: 58,
  senior: 38
}, {
  month: 'Mar',
  studenti: 135,
  professionisti: 92,
  famiglie: 62,
  senior: 40
}, {
  month: 'Apr',
  studenti: 145,
  professionisti: 98,
  famiglie: 67,
  senior: 42
}];
const preferencesData = [{
  name: 'Prodotti eco-friendly',
  value: 234
}, {
  name: 'Esperienze sostenibili',
  value: 198
}, {
  name: 'Mobilità verde',
  value: 167
}, {
  name: 'Alimentazione bio',
  value: 145
}, {
  name: 'Energia rinnovabile',
  value: 123
}];
const habitsData = [{
  name: 'Riciclo quotidiano',
  frequency: 'Sempre',
  value: 89
}, {
  name: 'Trasporto pubblico',
  frequency: 'Spesso',
  value: 72
}, {
  name: 'Riduzione plastica',
  frequency: 'Sempre',
  value: 85
}, {
  name: 'Acquisti locali',
  frequency: 'Spesso',
  value: 68
}, {
  name: 'Risparmio energetico',
  frequency: 'Sempre',
  value: 91
}];
const actionsData = [{
  category: 'Mobilità',
  count: 456
}, {
  category: 'Riciclo',
  count: 389
}, {
  category: 'Energia',
  count: 312
}, {
  category: 'Alimentazione',
  count: 278
}, {
  category: 'Acquisti',
  count: 234
}];
const topActionsData = [{
  action: 'Utilizzo bici/monopattino',
  count: 234,
  impact: '+15 kg CO₂ risparmiati'
}, {
  action: 'Riciclo corretto rifiuti',
  count: 198,
  impact: '+12 kg rifiuti recuperati'
}, {
  action: 'Spesa a km zero',
  count: 167,
  impact: '+8 kg CO₂ risparmiati'
}, {
  action: 'Riduzione consumo acqua',
  count: 145,
  impact: '+450 L acqua risparmiati'
}, {
  action: 'Uso energia rinnovabile',
  count: 123,
  impact: '+20 kg CO₂ risparmiati'
}];
// Check if data meets privacy threshold
const PRIVACY_THRESHOLD = 30;
const totalUsers = segmentData.reduce((sum, seg) => sum + seg.value, 0);
const meetsThreshold = totalUsers >= PRIVACY_THRESHOLD;
const Insights = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'audience' | 'preferences' | 'actions'>('audience');
  const [activeAIWidget, setActiveAIWidget] = useState<string | null>(null);
  const PrivacyBanner = () => <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4" data-id="element-2334">
      <div className="flex items-start" data-id="element-2335">
        <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-2336" />
        <div data-id="element-2337">
          <h3 className="text-sm font-medium text-blue-900" data-id="element-2338">Dati Aggregati</h3>
          <p className="mt-1 text-sm text-blue-800" data-id="element-2339">
            Dati aggregati (soglia minima n≥30). Alcuni risultati possono essere
            nascosti per privacy.
          </p>
        </div>
      </div>
    </div>;
  const InsufficientDataCard = () => <div className="rounded-lg border border-gray-200 bg-white p-12" data-id="element-2340">
      <div className="text-center" data-id="element-2341">
        <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" data-id="element-2342" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2" data-id="element-2343">
          Dati Insufficienti
        </h2>
        <p className="text-gray-500 max-w-md mx-auto" data-id="element-2344">
          Servono almeno 30 utenti nel periodo selezionato.
        </p>
        <p className="text-sm text-gray-400 mt-2" data-id="element-2345">
          Utenti attuali: {totalUsers}
        </p>
      </div>
    </div>;
  // AI Suggestions for each widget - converted to AIRecommendationPanel format
  const getRecommendationsForWidget = (widgetKey: string) => {
    const widgetData: Record<string, any> = {
      segmentDistribution: {
        title: 'Suggerimenti AI: Distribuzione Segmenti',
        recommendations: [{
          id: '1',
          title: 'Crea premi per Studenti',
          type: 'Opportunità',
          diamondRange: 'Segmento 41%',
          reason: 'Segmento dominante. Offri sconti su trasporti pubblici, abbonamenti bike-sharing, o prodotti eco-friendly per università.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Attiva campagna Senior',
          type: 'Strategia',
          diamondRange: 'Segmento 12%',
          reason: 'Sottorappresentati. Crea premi su risparmio energetico domestico, prodotti per la casa sostenibili, o workshop educativi.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      segmentTrend: {
        title: 'Suggerimenti AI: Trend Segmenti',
        recommendations: [{
          id: '1',
          title: 'Capitalizza crescita Famiglie',
          type: 'Trend',
          diamondRange: '+22% ultimo mese',
          reason: 'Accelerazione forte. Crea bundle famiglia: kit prodotti casa sostenibile, esperienze weekend green, sconti spesa bio.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Prepara offerte estive Studenti',
          type: 'Previsione',
          diamondRange: 'Picco Maggio-Giugno',
          reason: 'Anticipa premi su mobilità estiva, viaggi sostenibili, eventi outdoor eco-friendly.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      preferences: {
        title: 'Suggerimenti AI: Preferenze',
        recommendations: [{
          id: '1',
          title: 'Espandi catalogo Eco-friendly',
          type: 'Opportunità',
          diamondRange: '234 preferenze',
          reason: 'Domanda altissima. Aggiungi: cosmetici bio, abbigliamento sostenibile, accessori riutilizzabili, prodotti zero-waste.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Crea esperienze uniche',
          type: 'Strategia',
          diamondRange: '198 preferenze (+18%)',
          reason: 'Offri: workshop eco-design, visite aziende sostenibili, cooking class bio, tour natura guidati.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      habits: {
        title: 'Suggerimenti AI: Abitudini',
        recommendations: [{
          id: '1',
          title: 'Premia abitudini consolidate',
          type: 'Rinforzo',
          diamondRange: '89-91% sempre',
          reason: 'Risparmio energetico e riciclo già forti. Offri: dispositivi smart home, compostiere domestiche, gadget monitoraggio consumi.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Stimola mobilità sostenibile',
          type: 'Incentivo',
          diamondRange: '72% spesso',
          reason: 'Crea: abbonamenti scontati, bonus bici/monopattini, cashback car-sharing elettrico.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      actionsByCategory: {
        title: 'Suggerimenti AI: Azioni per Categoria',
        recommendations: [{
          id: '1',
          title: 'Potenzia categoria Mobilità',
          type: 'Opportunità',
          diamondRange: '456 azioni (+27%)',
          reason: 'Leader assoluto. Aggiungi sfide: "30 giorni in bici", "Zero auto per una settimana", premi per km sostenibili percorsi.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Attiva categoria Acquisti',
          type: 'Miglioramento',
          diamondRange: '234 azioni',
          reason: 'Crea: cashback spesa km0, sconti mercati locali, punti extra per prodotti sfusi, partnership negozi bio.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      },
      topActions: {
        title: 'Suggerimenti AI: Top Azioni',
        recommendations: [{
          id: '1',
          title: 'Gamifica top azioni',
          type: 'Strategia',
          diamondRange: '234 completamenti',
          reason: 'Top 3 molto popolari. Crea: badge progressivi, classifiche mensili, premi milestone (10/50/100 azioni), sfide team.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }, {
          id: '2',
          title: 'Diversifica azioni disponibili',
          type: 'Espansione',
          diamondRange: '60% impatto su top 3',
          reason: 'Aggiungi: compostaggio, riparazione oggetti, donazioni, volontariato ambientale.',
          ctaLabel: 'Crea premio',
          onCtaClick: () => {
            navigate('/rewards/new');
            setActiveAIWidget(null);
          }
        }]
      }
    };
    return widgetData[widgetKey] || null;
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-2346">
      <div className="w-20 flex-shrink-0" data-id="element-2347">
        <Sidebar data-id="element-2348" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2349">
        <Topbar data-id="element-2350" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-2351">
          <PageHeader title="Insight" subtitle="Preferenze e azioni degli utenti (dati aggregati)." data-id="element-2352" />

          <PrivacyBanner data-id="element-2353" />

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6" data-id="element-2354">
            <nav className="-mb-px flex space-x-8" data-id="element-2355">
              <button onClick={() => setActiveTab('audience')} className={`flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'audience' ? 'border-greenback text-greenback' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2356">
                <Users className="h-4 w-4 mr-2" data-id="element-2357" />
                Pubblico
              </button>
              <button onClick={() => setActiveTab('preferences')} className={`flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'preferences' ? 'border-greenback text-greenback' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2358">
                <Heart className="h-4 w-4 mr-2" data-id="element-2359" />
                Preferenze e Abitudini
              </button>
              <button onClick={() => setActiveTab('actions')} className={`flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'actions' ? 'border-greenback text-greenback' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2360">
                <Leaf className="h-4 w-4 mr-2" data-id="element-2361" />
                Azioni Sostenibili
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {!meetsThreshold ? <InsufficientDataCard data-id="element-2362" /> : <div className="space-y-6" data-id="element-2363">
              {/* Audience Tab */}
              {activeTab === 'audience' && <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2364">
                    {/* Segment Distribution */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-2365">
                      <div className="flex items-center justify-between mb-4" data-id="element-2366">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-2367">
                          Distribuzione Segmenti
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-2368">
                          <button onClick={() => setActiveAIWidget('segmentDistribution')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-2369">
                            <span className="text-sm" data-id="element-2370">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="h-80" data-id="element-2371">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-2372">
                          <PieChart data-id="element-2373">
                            <Pie data={segmentData} cx="50%" cy="50%" labelLine={false} label={({
                        name,
                        percent
                      }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={100} fill="#8884d8" dataKey="value" data-id="element-2374">
                              {segmentData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-2375" />)}
                            </Pie>
                            <Tooltip data-id="element-2376" />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-3" data-id="element-2377">
                        {segmentData.map((segment, index) => <div key={index} className="flex items-center" data-id="element-2378">
                            <div className="w-3 h-3 rounded-full mr-2" style={{
                      backgroundColor: segment.color
                    }} data-id="element-2379"></div>
                            <span className="text-sm text-gray-600" data-id="element-2380">
                              {segment.name}: {segment.value}
                            </span>
                          </div>)}
                      </div>
                    </div>

                    {/* Segment Trends */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-2381">
                      <div className="flex items-center justify-between mb-4" data-id="element-2382">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-2383">
                          Trend Segmenti nel Tempo
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-2384">
                          <button onClick={() => setActiveAIWidget('segmentTrend')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-2385">
                            <span className="text-sm" data-id="element-2386">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="h-80" data-id="element-2387">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-2388">
                          <LineChart data={segmentTrendData} data-id="element-2389">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-2390" />
                            <XAxis dataKey="month" stroke="#6B7280" fontSize={12} data-id="element-2391" />
                            <YAxis stroke="#6B7280" fontSize={12} data-id="element-2392" />
                            <Tooltip contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px'
                      }} data-id="element-2393" />
                            <Legend data-id="element-2394" />
                            <Line type="monotone" dataKey="studenti" stroke="#1a4d2e" strokeWidth={2} name="Studenti" data-id="element-2395" />
                            <Line type="monotone" dataKey="professionisti" stroke="#2d7a4f" strokeWidth={2} name="Professionisti" data-id="element-2396" />
                            <Line type="monotone" dataKey="famiglie" stroke="#4aa770" strokeWidth={2} name="Famiglie" data-id="element-2397" />
                            <Line type="monotone" dataKey="senior" stroke="#7bc99c" strokeWidth={2} name="Senior" data-id="element-2398" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2399">
                    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2400">
                      <p className="text-sm text-gray-500" data-id="element-2401">Utenti Totali</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2402">
                        {totalUsers}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2403">
                      <p className="text-sm text-gray-500" data-id="element-2404">
                        Segmento Principale
                      </p>
                      <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2405">
                        Studenti
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2406">
                      <p className="text-sm text-gray-500" data-id="element-2407">Crescita Mensile</p>
                      <p className="text-2xl font-bold text-green-600 mt-1" data-id="element-2408">
                        +8.5%
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" data-id="element-2409">
                      <p className="text-sm text-gray-500" data-id="element-2410">Tasso Attività</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2411">
                        73%
                      </p>
                    </div>
                  </div>
                </>}

              {/* Preferences Tab */}
              {activeTab === 'preferences' && <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2412">
                    {/* Main Preferences */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-2413">
                      <div className="flex items-center justify-between mb-4" data-id="element-2414">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-2415">
                          Preferenze Principali
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-2416">
                          <button onClick={() => setActiveAIWidget('preferences')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-2417">
                            <span className="text-sm" data-id="element-2418">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="h-80" data-id="element-2419">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-2420">
                          <BarChart data={preferencesData} layout="vertical" data-id="element-2421">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-2422" />
                            <XAxis type="number" stroke="#6B7280" fontSize={12} data-id="element-2423" />
                            <YAxis type="category" dataKey="name" stroke="#6B7280" fontSize={12} width={150} data-id="element-2424" />
                            <Tooltip contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px'
                      }} data-id="element-2425" />
                            <Bar dataKey="value" fill="#1a4d2e" radius={[0, 4, 4, 0]} data-id="element-2426" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Habits */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-2427">
                      <div className="flex items-center justify-between mb-4" data-id="element-2428">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-2429">
                          Abitudini Dichiarate
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-2430">
                          <button onClick={() => setActiveAIWidget('habits')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-2431">
                            <span className="text-sm" data-id="element-2432">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="space-y-4" data-id="element-2433">
                        {habitsData.map((habit, index) => <div key={index} data-id="element-2434">
                            <div className="flex justify-between items-center mb-2" data-id="element-2435">
                              <span className="text-sm font-medium text-gray-900" data-id="element-2436">
                                {habit.name}
                              </span>
                              <div className="flex items-center" data-id="element-2437">
                                <span className="text-xs text-gray-500 mr-2" data-id="element-2438">
                                  {habit.frequency}
                                </span>
                                <span className="text-sm font-semibold text-greenback" data-id="element-2439">
                                  {habit.value}%
                                </span>
                              </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2440">
                              <div className="bg-greenback h-2 rounded-full" style={{
                        width: `${habit.value}%`
                      }} data-id="element-2441"></div>
                            </div>
                          </div>)}
                      </div>
                      <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-2442">
                        <p className="text-xs text-gray-500" data-id="element-2443">
                          Scala frequenza: Sempre (80-100%) • Spesso (60-79%) •
                          A volte (40-59%) • Raramente (&lt;40%)
                        </p>
                      </div>
                    </div>
                  </div>
                </>}

              {/* Actions Tab */}
              {activeTab === 'actions' && <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2444">
                    {/* Actions by Category */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-2445">
                      <div className="flex items-center justify-between mb-4" data-id="element-2446">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-2447">
                          Azioni Completate per Categoria
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-2448">
                          <button onClick={() => setActiveAIWidget('actionsByCategory')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-2449">
                            <span className="text-sm" data-id="element-2450">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="h-80" data-id="element-2451">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-2452">
                          <BarChart data={actionsData} data-id="element-2453">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-2454" />
                            <XAxis dataKey="category" stroke="#6B7280" fontSize={12} data-id="element-2455" />
                            <YAxis stroke="#6B7280" fontSize={12} data-id="element-2456" />
                            <Tooltip contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px'
                      }} data-id="element-2457" />
                            <Bar dataKey="count" fill="#1a4d2e" radius={[4, 4, 0, 0]} data-id="element-2458" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Top Actions */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-2459">
                      <div className="flex items-center justify-between mb-4" data-id="element-2460">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-2461">
                          Top Azioni del Periodo
                        </h3>
                        <div style={{
                    background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 50%, rgba(116, 10, 214, 1) 100%)',
                    padding: '2px',
                    borderRadius: '100px'
                  }} data-id="element-2462">
                          <button onClick={() => setActiveAIWidget('topActions')} className="text-black transition-colors flex items-center gap-1.5 bg-white px-3 py-1.5 hover:bg-gray-50" style={{
                      borderRadius: '100px'
                    }} title="AI Insights" data-id="element-2463">
                            <span className="text-sm" data-id="element-2464">Suggerimenti AI</span>
                          </button>
                        </div>
                      </div>
                      <div className="space-y-3" data-id="element-2465">
                        {topActionsData.map((action, index) => <div key={index} className="flex items-start p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-2466">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-greenback text-white flex items-center justify-center font-semibold text-sm mr-3" data-id="element-2467">
                              {index + 1}
                            </div>
                            <div className="flex-1" data-id="element-2468">
                              <div className="flex items-center justify-between" data-id="element-2469">
                                <p className="font-medium text-gray-900 text-sm" data-id="element-2470">
                                  {action.action}
                                </p>
                                <span className="text-sm font-semibold text-greenback" data-id="element-2471">
                                  {action.count}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1 flex items-center" data-id="element-2472">
                                <TrendingUp className="h-3 w-3 mr-1" data-id="element-2473" />
                                {action.impact}
                              </p>
                            </div>
                          </div>)}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2474">
                        <div className="flex items-center justify-between text-sm" data-id="element-2475">
                          <span className="text-gray-500" data-id="element-2476">Totale azioni:</span>
                          <span className="font-semibold text-gray-900" data-id="element-2477">
                            1,669
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Impact Summary */}
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200 p-6" data-id="element-2478">
                    <div className="flex items-start" data-id="element-2479">
                      <Leaf className="h-6 w-6 text-green-600 mt-1 mr-3 flex-shrink-0" data-id="element-2480" />
                      <div data-id="element-2481">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2482">
                          Impatto Aggregato del Periodo
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4" data-id="element-2483">
                          <div data-id="element-2484">
                            <p className="text-sm text-gray-600" data-id="element-2485">
                              CO₂ Risparmiata
                            </p>
                            <p className="text-2xl font-bold text-green-600" data-id="element-2486">
                              +68 kg
                            </p>
                          </div>
                          <div data-id="element-2487">
                            <p className="text-sm text-gray-600" data-id="element-2488">
                              Rifiuti Recuperati
                            </p>
                            <p className="text-2xl font-bold text-green-600" data-id="element-2489">
                              +45 kg
                            </p>
                          </div>
                          <div data-id="element-2490">
                            <p className="text-sm text-gray-600" data-id="element-2491">
                              Acqua Risparmiata
                            </p>
                            <p className="text-2xl font-bold text-green-600" data-id="element-2492">
                              +1,200 L
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>}
            </div>}
        </main>
      </div>

      {/* AI Sidebar - Using consistent AIRecommendationPanel */}
      {meetsThreshold && activeAIWidget && <AISidebar isOpen={!!activeAIWidget} onClose={() => setActiveAIWidget(null)} data-id="element-2493">
          {(() => {
        const widgetData = getRecommendationsForWidget(activeAIWidget);
        return widgetData ? <AIRecommendationPanel title={widgetData.title} recommendations={widgetData.recommendations} lastUpdated="Ultimi 30 giorni" data-id="element-2494" /> : null;
      })()}
        </AISidebar>}
    </div>;
};
export default Insights;